import type { FollowUp, FollowUpSeverity } from "./followups-api.js";

export type AttentionGroupKey =
  "missed_checks" | "needs_decision" | "worth_a_look";

export const attentionGroupOrder: readonly AttentionGroupKey[] = [
  "needs_decision",
  "worth_a_look",
  "missed_checks",
];

export const attentionGroupLabels: Record<AttentionGroupKey, string> = {
  missed_checks: "Missed checks",
  needs_decision: "Needs a decision",
  worth_a_look: "Worth a look",
};

const severityRank: Record<FollowUpSeverity, number> = {
  critical: 0,
  major: 1,
  minor: 2,
};

export function isAttentionGroup(
  value: string | null | undefined,
): value is AttentionGroupKey {
  return (
    value === "missed_checks" ||
    value === "needs_decision" ||
    value === "worth_a_look"
  );
}

// plan/11 §5: failures, critical items, rejected deliveries and incidents need
// a decision; missed checks stand apart; everything else is worth a look.
export function attentionGroupOf(
  item: Pick<FollowUp, "severity" | "trigger_kind">,
): AttentionGroupKey {
  if (item.trigger_kind === "missed_task") return "missed_checks";
  if (
    item.trigger_kind === "rule_fail" ||
    item.trigger_kind === "rejected_delivery" ||
    item.trigger_kind === "incident" ||
    item.severity === "critical"
  )
    return "needs_decision";
  return "worth_a_look";
}

// Severity first, then the oldest item.
export function compareAttention(
  a: Pick<FollowUp, "created_at" | "severity">,
  b: Pick<FollowUp, "created_at" | "severity">,
): number {
  const bySeverity = severityRank[a.severity] - severityRank[b.severity];
  if (bySeverity !== 0) return bySeverity;
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
}

export interface AttentionGroup {
  items: FollowUp[];
  key: AttentionGroupKey;
  label: string;
}

export function groupFollowUps(items: readonly FollowUp[]): AttentionGroup[] {
  const buckets: Record<AttentionGroupKey, FollowUp[]> = {
    missed_checks: [],
    needs_decision: [],
    worth_a_look: [],
  };
  for (const item of items) buckets[attentionGroupOf(item)].push(item);
  return attentionGroupOrder.map((key) => ({
    items: buckets[key].sort(compareAttention),
    key,
    label: attentionGroupLabels[key],
  }));
}

export function needsDecision(items: readonly FollowUp[]): FollowUp[] {
  return items
    .filter((item) => attentionGroupOf(item) === "needs_decision")
    .sort(compareAttention);
}
